<template>
  <Teleport to="body">
    <div
      aria-modal="true"
      class="slideshow-viewer"
      role="dialog"
      v-if="viewer.open"
      ref="root"
      :aria-label="`${forFilm('heading', 'lifeTitle')} · ${name}`"
      :class="{ 'is-fullscreen': isFullscreen, 'is-idle': idle }"
      :style="swipeBackdrop"
    >
      <div
        class="slideshow-viewer__box"
        tabindex="-1"
        ref="box"
        :class="{ 'is-swipe-settling': swipe.settling, 'is-swiping': swipe.active }"
        :style="swipeBox"
      >
        <div class="slideshow-viewer__head" :inert="phoneShareOpen">
          <div class="slideshow-viewer__buttons">
            <!-- Phones: a choice of the film or the link; computers: the share card. -->
            <button
              aria-haspopup="dialog"
              class="slideshow-viewer__button slideshow-viewer__button--text"
              type="button"
              ref="shareButton"
              :aria-expanded="sharing || choosing"
              @click="onShareButton"
            >
              <SlideshowIcon name="share" />
              <span>{{ texts.share }}</span>
            </button>
            <button
              class="slideshow-viewer__button"
              type="button"
              :aria-label="texts.close"
              @click="closeButton"
            >
              <SlideshowIcon name="close" />
            </button>
          </div>
        </div>

        <SlideshowSettings
          class="slideshow-viewer__panel is-settings"
          v-if="settingsOpen"
          ref="settingsPanel"
          v-model:bookends="bookends"
          v-model:long="long"
          v-model:loop="loop"
          v-model:qr="showQr"
          :lengths="lengths"
        />

        <div
          class="slideshow-viewer__stage"
          ref="stage"
          :inert="phoneShareOpen"
        >
          <canvas
            aria-hidden="true"
            class="slideshow-viewer__canvas"
            ref="canvas"
          />
          <p aria-live="polite" class="screen-reader-text">{{ caption }}</p>
          <div class="slideshow-viewer__qr" v-if="showQr && qr">
            <div v-html="qr" />
            <span>{{ texts.qrCaption }}</span>
          </div>
          <!-- There, hidden, all along: the end card makes room for it before it shows. -->
          <SlideshowEndShare
            v-if="!loop && content"
            :class="{ 'is-dark': darkEnd }"
            :film-making="filmMaking"
            :film-percent="filmPercent"
            :film-state="filmState"
            :film-video="shareFilm.videoMode.value"
            :name="content.deceased.name"
            :page-url="pageUrl"
            :shares-files="sharesFiles"
            :shown="atEnd && !sharing"
            :style="
              endBottom === null
                ? undefined
                : {
                    '--end-fade': `${endFade}s`,
                    bottom: 'auto',
                    top: `${endBottom}px`,
                  }
            "
            @add-photos="addPhotos"
            @download="downloadFilm"
            @height="makeEndRoom"
            @replay="playAgain"
            @share="shareFromPhone('end_card')"
            @share-link="shareLinkFromPhone('end_card')"
          />
          <p aria-live="polite" class="slideshow-viewer__toast" v-if="toast">
            {{ toast }}
          </p>
          <SlideshowControls
            class="slideshow-viewer__controls"
            :duration="duration"
            :playing="playing"
            :time="time"
            @seek="seek"
            @toggle="togglePlay"
          >
            <template #start>
              <button
                class="slideshow-controls__button"
                type="button"
                :aria-label="muted ? texts.soundOn : texts.soundOff"
                :aria-pressed="!muted"
                @click="toggleSound"
              >
                <SlideshowIcon :name="muted ? 'soundOff' : 'soundOn'" />
              </button>
            </template>
            <template #end>
              <button
                aria-haspopup="dialog"
                class="slideshow-controls__button"
                type="button"
                v-if="!phone"
                ref="settingsButton"
                :aria-expanded="settingsOpen"
                :aria-label="texts.settings"
                :title="texts.settings"
                @click="toggleSettings"
              >
                <SlideshowIcon name="settings" />
              </button>
              <button
                class="slideshow-controls__button"
                type="button"
                v-if="canFullscreen"
                :aria-label="isFullscreen ? texts.exitFullscreen : texts.fullscreen"
                :title="isFullscreen ? texts.exitFullscreen : texts.fullscreen"
                @click="toggleFullscreen"
              >
                <SlideshowIcon :name="isFullscreen ? 'unfull' : 'full'" />
              </button>
            </template>
          </SlideshowControls>
        </div>

        <SlideshowShareChoice
          class="slideshow-viewer__panel is-share"
          v-if="choosing"
          :film-percent="filmPercent"
          :film-state="filmState"
          @film="shareFromPhone('share_panel')"
          @link="shareLinkFromPhone('share_panel')"
        />

        <template v-if="sharing && content">
          <SlideshowSharePhone
            v-if="sharesFiles"
            ref="sharePhone"
            :content="content"
            :exporter="shareFilm"
            :name="name"
            :page-url="pageUrl"
            @close="closeShare"
          />
          <SlideshowShareCard
            class="slideshow-viewer__panel is-share"
            v-else
            ref="sharePanel"
            :content="content"
            :exporter="shareFilm"
            :name="name"
            :page-url="pageUrl"
            :section-id="sectionId"
          />
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
  import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    shallowRef,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import {
    CONTROLS_IDLE_MS,
    useCaptionLift,
  } from "../../composables/useControls";
  import { useFilmExport } from "../../composables/useFilmExport";
  import { useShareLink } from "../../composables/useShareLink";
  import {
    assets,
    closeViewer,
    forFilm,
    lifeStoryHash,
    loadPageContent,
    musicFor,
    PHONE_QUERY,
    quickContent,
    slideshowData,
    slideshowTexts,
    useMediaQuery,
    usePrefersReducedMotion,
    viewer,
  } from "../../composables/useSlideshow";
  import { useWakeLock } from "../../composables/useWakeLock";
  import { MEDIA_INITIAL } from "../../createCommentStepsHelper";
  import {
    LIFE_SELECTION,
    lifeStartTime,
    makeFilm,
    playlistTexts,
    slideIndexOf,
    spokenCaption,
  } from "../../slideshow/film";
  import { FULL_WIDTH, withWidth } from "../../slideshow/imgix";
  import { MusicTrack } from "../../slideshow/music";
  import { SlideshowPlayer } from "../../slideshow/player";
  import type { Format, PageContent, Playlist } from "../../slideshow/playlist";
  import { LONGER_FILM, sprintf } from "../../slideshow/playlist";
  import {
    canShareFiles,
    download,
    LIFE_STORY_CONTENT,
    qrAddress,
    qrSvg,
    shareFile,
    shareLink,
  } from "../../slideshow/share";
  import { type SlideshowEvent, track, trackFilm } from "../../slideshow/track";
  import type { Timeline } from "../../slideshow/types";
  import SlideshowControls from "./SlideshowControls.vue";
  import SlideshowEndShare from "./SlideshowEndShare.vue";
  import SlideshowIcon from "./SlideshowIcon.vue";
  import SlideshowSettings from "./SlideshowSettings.vue";
  import SlideshowShareCard from "./SlideshowShareCard.vue";
  import SlideshowShareChoice from "./SlideshowShareChoice.vue";
  import SlideshowSharePhone from "./SlideshowSharePhone.vue";

  type ShareSurface = SlideshowEvent["surface"];

  const texts = slideshowTexts();
  const data = slideshowData();
  const name = data?.deceased.name ?? "";
  const HASH = /#.*$/;
  /** A seek this close to the end ends the film, as if it had played there. */
  const END_SLACK_SECONDS = 0.3;
  /** The controls' bar: the end card and its buttons centre in the space above it, CSS px. */
  const END_CONTROLS = 64;
  // The memorial page, and the page with the film opening on arrival.
  const memorialUrl = (data?.pageUrl || window.location.href).replace(HASH, "");
  const sectionId = computed(() =>
    viewer.kind === "life" ? lifeStoryHash() : (data?.sectionId ?? "bildspel")
  );
  const pageUrl = computed(() =>
    data ? `${memorialUrl}#${sectionId.value}` : window.location.href
  );

  const root = ref<HTMLElement | null>(null);
  const box = ref<HTMLElement | null>(null);
  const stage = ref<HTMLElement | null>(null);
  const canvas = ref<HTMLCanvasElement | null>(null);
  const settingsButton = ref<HTMLButtonElement | null>(null);
  const shareButton = ref<HTMLButtonElement | null>(null);
  const sharePanel = ref<{ $el: HTMLElement } | null>(null);
  const sharePhone = ref<{ $el: HTMLElement } | null>(null);
  const settingsPanel = ref<{ $el: HTMLElement } | null>(null);
  const content = shallowRef<PageContent | null>(null);
  const playlist = shallowRef<Playlist | null>(null);
  let player: SlideshowPlayer | null = null;
  let music: MusicTrack | null = null;
  let idleTimer = 0;
  let wasPlaying = false;
  const wakeLock = useWakeLock();

  const playing = ref(false);
  const time = ref(0);
  const duration = ref(0);
  const ended = ref(false);
  // When the end card starts to fade in, for how long, and where its logo ends: the buttons
  // fade in with it, under the logo.
  const endCardAt = ref<number | null>(null);
  const endFade = ref(0.6);
  // Arkiv ends on its dark table: the share row under the logo turns light.
  const darkEnd = ref(false);
  const endBottom = ref<number | null>(null);
  const caption = ref("");
  const idle = ref(false);
  // The settings: loop, a QR code to the page, and the longer film, each the visitor's choice.
  const settingsOpen = ref(false);
  const loop = ref(false);
  // Looping, the name card and the credits can come round every time or only the photos.
  const bookends = ref(true);
  const showQr = ref(false);
  const long = ref(false);
  const qr = ref("");
  // Music is off until the visitor turns it on.
  const muted = ref(true);
  const sharing = ref(false);
  const isFullscreen = ref(false);
  const canFullscreen =
    typeof document !== "undefined" && !!document.fullscreenEnabled;
  const phone = useMediaQuery(PHONE_QUERY);
  // Sharing follows the device, not the width: a touch screen whose share sheet takes files
  // shares the film; everything else shares the link.
  const sharesFiles =
    window.matchMedia("(pointer: coarse) and (hover: none)").matches &&
    canShareFiles();
  /** The phone's share view covers the viewer: what's under it is out of reach. */
  const phoneShareOpen = computed(() => sharing.value && sharesFiles);
  const reducedMotion = usePrefersReducedMotion();
  const store = useStore();
  const format = computed(() => (phone.value ? "portrait" : "landscape"));

  // ----- the film: made ahead on phones, on request on computers -----

  /**
   * The film is made once the first photo has arrived on screen, so the opening plays
   * smoothly: making it loads every photo and draws frames beside the player.
   */
  const FILM_START_MS = 2000;
  const FILM_AFTER_LANDING_MS = 500;

  /** Milliseconds until the first photo from `from` (seconds) has come in. */
  function filmStartDelay(timeline: Timeline, from: number) {
    const first = timeline.segments.find(
      (seg) =>
        seg.start + seg.td > from &&
        (seg.slide.kind === "photo" || seg.slide.kind === "clip")
    );
    const landed = first ? (first.start + first.td - from) * 1000 : 0;
    return Math.max(FILM_START_MS, landed + FILM_AFTER_LANDING_MS);
  }
  const TOAST_MS = 6000;
  // Phones post to stories and chats, which show a standing film well; computers download.
  const FILM_FORMAT: Format = sharesFiles ? "portrait" : "landscape";
  const shareFilm = useFilmExport(
    () => content.value as PageContent,
    () => name,
    sharesFiles
  );
  /** The share button last used on a phone (the end card or the panel under Dela). */
  let shareSurface: ShareSurface = "end_card";
  const { copyLink, filmText, linkText, urlFor } = useShareLink(
    () => pageUrl.value,
    () => name,
    () => shareSurface
  );
  const filmMaking = computed(
    () => shareFilm.videoMode.value && shareFilm.making.value
  );
  const filmPercent = computed(() => shareFilm.percent.value);
  /**
   * Someone tapped Ladda ner before the film was ready: only then does the button show the
   * progress, and "ready" once it's done (the share sheet needs that next tap).
   */
  const filmWaited = ref(false);
  const filmState = computed(() => {
    if (!filmWaited.value) {
      return "idle";
    }
    return filmMaking.value ? "making" : "ready";
  });
  const toast = ref("");
  /** The phone's small choice under Dela: the film or the link. */
  const choosing = ref(false);
  let filmTimer = 0;
  let toastTimer = 0;

  // The film couldn't be made after all: back to the plain button.
  watch(filmMaking, (making) => {
    if (!(making || shareFilm.readyFile(FILM_FORMAT))) {
      filmWaited.value = false;
    }
  });

  function onShareButton() {
    if (sharesFiles && shareFilm.videoMode.value) {
      settingsOpen.value = false;
      choosing.value = !choosing.value;
    } else {
      toggleShare();
    }
  }

  /** The link alone, through the phone's share sheet; copied where there is none. */
  async function shareLinkFromPhone(surface: ShareSurface) {
    shareSurface = surface;
    choosing.value = false;
    const outcome = await shareLink(urlFor("link"), linkText.value);
    if (outcome === "shared") {
      track({ action: "link_shared", platform: "os", surface });
    } else if (outcome === "unsupported") {
      await copyLink();
      showToast(texts.copied);
    }
  }

  /** Start on the film ahead of time, unless the visitor saves data. */
  function prepareFilm() {
    const saveData = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection?.saveData;
    if (sharesFiles && shareFilm.videoMode.value && !saveData) {
      shareFilm.ensureFile(FILM_FORMAT, "open");
    }
  }

  function showToast(text: string) {
    toast.value = text;
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.value = "";
    }, TOAST_MS);
  }

  /**
   * Phones share the film itself. Ready: the share sheet opens on this tap. Being made: the
   * button waits, filling up, and the next tap shares. No film here: the link, in the share view.
   */
  async function shareFromPhone(surface: ShareSurface) {
    shareSurface = surface;
    if (!shareFilm.videoMode.value) {
      openShare();
      return;
    }
    const file = shareFilm.readyFile(FILM_FORMAT);
    if (!file) {
      filmWaited.value = true;
      if (!shareFilm.making.value) {
        shareFilm.ensureFile(FILM_FORMAT, "share");
      }
      return;
    }
    filmWaited.value = false;
    choosing.value = false;
    // Straight to the share sheet: nothing else may use the tap first (Safari counts a
    // clipboard write as using it, and then refuses the share sheet).
    track({ action: "share_open", format: FILM_FORMAT, surface });
    // With a message and the link, which chats and mail show beside the film; Instagram
    // and Spara video take the film alone.
    const outcome = await shareFile(file, filmText.value);
    if (outcome === "shared") {
      track({
        action: "video_shared",
        format: FILM_FORMAT,
        platform: "os",
        surface,
      });
      showToast(sprintf(texts.sharedThanks, name));
    } else if (outcome === "needs-tap") {
      // The browser still wants a fresh tap: the button stays at Klar for it.
      filmWaited.value = true;
    } else if (outcome === "unsupported") {
      download(file);
      showToast(texts.savedDownloads);
      track({
        action: "video_downloaded",
        format: FILM_FORMAT,
        platform: "os-fallback",
        surface,
      });
    }
  }

  /** Computers: the film is made on the click, then downloads by itself. */
  async function downloadFilm() {
    if (shareFilm.making.value) {
      return;
    }
    const file = await shareFilm.ensureFile(FILM_FORMAT, "share");
    if (file) {
      download(file);
      track({
        action: "video_downloaded",
        format: FILM_FORMAT,
        surface: "end_card",
      });
    }
  }

  /**
   * The film's settings. `other` is the length that isn't the film's own: the slideshow's
   * longer film, or the life story's selection (it opens on the whole life).
   */
  function settingsFor(other: boolean) {
    const loopOnly = loop.value && !bookends.value ? { bookends: false } : {};
    if (viewer.kind === "life") {
      return {
        eyebrow: texts.lifeTitle,
        kind: "life" as const,
        ...(other ? LIFE_SELECTION : {}),
        ...loopOnly,
      };
    }
    return {
      // The photo the viewer was opened on is always in the film, even outside the selection.
      include: viewer.startId ? [viewer.startId] : [],
      ...(other ? LONGER_FILM : {}),
      ...loopOnly,
    };
  }

  function film(c: PageContent) {
    const f = makeFilm(
      c,
      format.value,
      reducedMotion.value,
      settingsFor(long.value)
    );
    playlist.value = f.playlist;
    const end = f.timeline.segments
      .filter((s) => s.slide.kind === "endcard")
      .pop();
    endCardAt.value = end ? end.start : null;
    endFade.value = end?.td || 0.6;
    darkEnd.value = f.timeline.template === "arkiv";
    return f.timeline;
  }

  /** Lay the film out again (new content, shape or length), staying on the same slide. */
  function relayout(c: PageContent) {
    player?.relayout(film(c));
    sync();
  }

  /** The selection and the longer film side by side, when the page has more than the selection. */
  const lengths = computed(() => {
    const c = content.value;
    if (!c) {
      return null;
    }
    const measure = (other: boolean) =>
      makeFilm(c, "landscape", false, settingsFor(other));
    // The life story's own length is the longer one; the slideshow's the selection.
    const life = viewer.kind === "life";
    const longer = measure(!life);
    const selection = measure(life);
    if (longer.playlist.media <= selection.playlist.media) {
      return null;
    }
    return {
      long: longer.timeline.duration,
      short: selection.timeline.duration,
    };
  });

  function sync() {
    if (!player) {
      return;
    }
    playing.value = player.playing;
    time.value = player.time;
    duration.value = player.duration;
  }

  // ----- how far the film is watched -----

  const PROGRESS_MARKS = [25, 50, 75];
  /** The marks passed since the viewer opened; 100 is the end card. */
  const passed = new Set<number>();
  let lastTime = 0;

  /** A mark counts when the film plays across it, not when a seek jumps over it. */
  function noteProgress(t: number, d: number) {
    const before = lastTime;
    const played = t > before && t - before < 1;
    const crossed = (at: number) => played && before < at && t >= at;
    lastTime = t;
    if (!d) {
      return;
    }
    for (const mark of PROGRESS_MARKS) {
      if (!passed.has(mark) && crossed((d * mark) / 100)) {
        passed.add(mark);
        track({ action: "progress", percent: mark });
      }
    }
    if (!passed.has(100) && crossed(endCardAt.value ?? d - 1)) {
      passed.add(100);
      track({ action: "complete" });
    }
  }

  function trackSetting(setting: string, value: string) {
    track({ action: "setting_changed", setting, value });
  }

  async function start() {
    const quick = quickContent();
    if (!(quick && canvas.value)) {
      return;
    }
    content.value = quick;
    const timeline = film(quick);
    player = new SlideshowPlayer(canvas.value, timeline, {
      assets,
      captions: true,
      loop: loop.value,
      onEnd: () => {
        ended.value = true;
        idle.value = false;
        sync();
      },
      onFrame: (t, d) => {
        noteProgress(t, d);
        time.value = t;
        duration.value = d;
        endBottom.value = player?.layout.endBottom ?? null;
      },
      onSlide: (_i, seg) => {
        caption.value = spokenCaption(seg.slide);
      },
      reducedMotion: reducedMotion.value,
    });
    player.sound = !muted.value;
    player.captionLift = captionLift.target();
    if (viewer.kind === "life" && viewer.chapter !== null) {
      player.seek(
        lifeStartTime(timeline, viewer.chapter, playlistTexts(texts))
      );
    } else if (viewer.startTime !== null) {
      player.seek(viewer.startTime);
    } else if (viewer.startId) {
      // Opened from a photo (the thumbnail strip): start on that photo itself.
      player.showPhoto(
        slideIndexOf(playlist.value as Playlist, viewer.startId)
      );
    }
    player.play();
    sync();
    passed.clear();
    trackFilm(viewer.kind);
    track({ action: "play", source: viewer.source });
    filmTimer = window.setTimeout(
      prepareFilm,
      filmStartDelay(timeline, player.time)
    );

    const musicUrl = musicFor(quick, viewer.kind);
    if (musicUrl) {
      music = new MusicTrack(musicUrl);
      music.muted = muted.value;
      music.unlock();
      music.start(() => ({
        loop: loop.value,
        // Quiet while the share sheet is open. It plays on while the film waits for a slow
        // photo: pausing and starting it again is what stutters.
        playing: !!player?.playing && !sharing.value,
        t: player?.time ?? 0,
        timeline: (player as SlideshowPlayer).film,
      }));
    }
    wakeLock.hold();

    // Faces and photo sizes arrive a moment later; the opening card looks the same either way.
    const full = await loadPageContent();
    if (full && player && viewer.open) {
      content.value = full;
      relayout(full);
    }
  }

  function stop() {
    captionLift.stop();
    // A film half made stops with the viewer; a finished one is kept for the visit.
    clearTimeout(filmTimer);
    shareFilm.cancel();
    filmWaited.value = false;
    choosing.value = false;
    player?.destroy();
    player = null;
    trackFilm("main");
    music?.destroy();
    music = null;
    wakeLock.release();
    clearTimeout(idleTimer);
    resetSwipe();
    // The length is per film: true is the life story's selection but the slideshow's longer film.
    long.value = false;
    playing.value = false;
    ended.value = false;
    sharing.value = false;
    settingsOpen.value = false;
  }

  function onVisibility() {
    if (!document.hidden && viewer.open && player?.playing) {
      wakeLock.hold();
    }
  }

  const captionLift = useCaptionLift(
    () => player,
    computed(() => !idle.value)
  );

  function wake() {
    idle.value = false;
    clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      if (player?.playing && !sharing.value && !settingsOpen.value) {
        idle.value = true;
      }
    }, CONTROLS_IDLE_MS);
  }

  function togglePlay() {
    if (!player) {
      return;
    }
    if (player.playing) {
      player.pause();
    } else {
      if (ended.value) {
        player.seek(0);
      }
      ended.value = false;
      music?.unlock();
      player.play();
    }
    sync();
    wake();
  }

  function seek(t: number) {
    player?.seek(t);
    // Dragged to the very end: the film is over, as if it had played there.
    ended.value =
      !!player && !loop.value && t >= player.duration - END_SLACK_SECONDS;
    if (ended.value) {
      player?.pause();
    }
    sync();
  }

  /** Sharing and playing again show as soon as the end card is in, unless the film loops. */
  const atEnd = computed(
    () =>
      ended.value ||
      (!loop.value && endCardAt.value !== null && time.value >= endCardAt.value)
  );

  /** The end card opens a gap under its title for the buttons, all centred together. */
  function makeEndRoom(height: number) {
    if (player) {
      player.endRoom = height;
      player.endFoot = END_CONTROLS;
      player.redraw();
    }
  }

  function playAgain() {
    ended.value = false;
    player?.seek(0);
    player?.play();
    sync();
  }

  let lastPointer = "mouse";

  function wakeFrom(e: PointerEvent) {
    lastPointer = e.pointerType;
    wake();
  }

  // Phones: swipe the film down to close it, as a photo is closed on a phone. It follows
  // the finger; a quarter of the screen or a flick closes it, less springs it back.
  const SWIPE_START = 10;
  const SWIPE_FLICK = 0.6;
  const swipe = reactive({ active: false, settling: false, y: 0 });
  let swipeFrom: { id: number; x: number; y: number; t: number } | null = null;
  let swallowClick = false;
  let swipeTimer = 0;

  /** The viewer closed another way (Esc, the X) mid-settle: nothing left to finish. */
  function resetSwipe() {
    clearTimeout(swipeTimer);
    swipeFrom = null;
    Object.assign(swipe, { active: false, settling: false, y: 0 });
  }

  const swipeBox = computed(() =>
    swipe.y > 0
      ? {
          transform: `translateY(${swipe.y}px) scale(${1 - Math.min(swipe.y / window.innerHeight, 1) * 0.08})`,
        }
      : undefined
  );

  const swipeBackdrop = computed(() =>
    swipe.y > 0
      ? {
          background: `rgb(0 0 0 / ${(0.6 * (1 - Math.min(swipe.y / (window.innerHeight * 0.6), 1))).toFixed(3)})`,
        }
      : undefined
  );

  function onSwipeStart(e: PointerEvent) {
    // A drag that ended outside the picture never got its click; this tap is a new one.
    swallowClick = false;
    if (
      e.pointerType !== "touch" ||
      !e.isPrimary ||
      settingsOpen.value ||
      sharing.value ||
      choosing.value ||
      swipe.settling ||
      (e.target as HTMLElement).closest(".slideshow-controls, button, input, a")
    ) {
      swipeFrom = null;
      return;
    }
    swipeFrom = { id: e.pointerId, t: e.timeStamp, x: e.clientX, y: e.clientY };
  }

  function onSwipeMove(e: PointerEvent) {
    if (!swipeFrom || e.pointerId !== swipeFrom.id) {
      return;
    }
    const dx = e.clientX - swipeFrom.x;
    const dy = e.clientY - swipeFrom.y;
    if (!swipe.active) {
      if (Math.abs(dx) > SWIPE_START && Math.abs(dx) > dy) {
        swipeFrom = null;
        return;
      }
      if (dy < SWIPE_START) {
        return;
      }
      swipe.active = true;
      stage.value?.setPointerCapture(e.pointerId);
    }
    swipe.y = Math.max(0, dy);
  }

  function onSwipeEnd(e: PointerEvent) {
    if (!swipeFrom || e.pointerId !== swipeFrom.id) {
      return;
    }
    const from = swipeFrom;
    swipeFrom = null;
    if (!swipe.active) {
      return;
    }
    swipe.active = false;
    swallowClick = true;
    const speed = swipe.y / Math.max(1, e.timeStamp - from.t);
    const closing =
      e.type === "pointerup" &&
      (swipe.y > window.innerHeight * 0.25 || speed > SWIPE_FLICK);
    if (closing && reducedMotion.value) {
      swipe.y = 0;
      close();
      return;
    }
    swipe.settling = true;
    swipe.y = closing ? window.innerHeight : 0;
    swipeTimer = window.setTimeout(() => {
      swipe.settling = false;
      if (closing) {
        swipe.y = 0;
        close();
      }
    }, 220);
  }

  function onStageClick(e: Event) {
    if (swallowClick) {
      swallowClick = false;
      return;
    }
    // A tap on the picture closes the phone's share choice, as one outside it would.
    if (choosing.value) {
      choosing.value = false;
      return;
    }
    // Clicks on the controls (the timeline, play) are theirs, not the picture's; with the
    // settings open, a click on the picture only closes them.
    if (
      settingsOpen.value ||
      (e.target as HTMLElement).closest(".slideshow-controls, button, input, a")
    ) {
      return;
    }
    // A mouse click on the picture plays or pauses, like a video; a tap only shows the controls.
    if (lastPointer === "mouse") {
      togglePlay();
    }
  }

  /** Move focus into a panel that just opened, to its first control. */
  function focusPanel(panel: () => { $el: HTMLElement } | null) {
    nextTick(() =>
      panel()
        ?.$el.querySelector<HTMLElement>("button, input, a[href]")
        ?.focus({ preventScroll: true })
    );
  }

  function toggleSettings() {
    settingsOpen.value = !settingsOpen.value;
    if (settingsOpen.value && sharing.value) {
      closeShare();
    }
    if (settingsOpen.value) {
      track({ action: "settings_open" });
      focusPanel(() => settingsPanel.value);
    }
    wake();
  }

  function closeSettings() {
    settingsOpen.value = false;
    nextTick(() => settingsButton.value?.focus({ preventScroll: true }));
  }

  watch(loop, (on) => {
    trackSetting("loop", on ? "on" : "off");
    if (player) {
      player.loop = on;
      // Looping, the film has no end to share from: the end card is centred again.
      if (on) {
        player.endRoom = 0;
        player.endFoot = 0;
        player.redraw();
      }
    }
    if (on && ended.value) {
      playAgain();
    }
  });

  // Only the visitor's own choice is a setting change, not the reset on close.
  watch(long, (on) => {
    if (viewer.open) {
      trackSetting(
        "length",
        on === (viewer.kind === "life") ? "selection" : "full"
      );
    }
  });
  watch(bookends, (on) => trackSetting("bookends", on ? "on" : "off"));

  watch([long, () => loop.value && !bookends.value], () => {
    if (content.value && viewer.open) {
      relayout(content.value);
    }
  });

  // Guests scanning it land on the memorial page itself, not in the viewer; their visits
  // show in GA4 as from the QR code.
  let qrFilm = "";
  watch(showQr, (on) => trackSetting("qr", on ? "on" : "off"));
  watch([showQr, () => viewer.kind, () => viewer.open], ([on, kind]) => {
    if (!on || (qr.value && qrFilm === kind)) {
      return;
    }
    qrFilm = kind;
    qr.value = "";
    const tag = kind === "life" ? LIFE_STORY_CONTENT : "";
    qrSvg(qrAddress(memorialUrl, data?.pageId ?? "", "", true, tag))
      .then((svg) => {
        if (qrFilm === kind) {
          qr.value = svg;
        }
      })
      .catch(() => undefined);
  });

  function toggleSound() {
    muted.value = !muted.value;
    if (!muted.value) {
      track({ action: "sound_on" });
    }
    if (music) {
      music.muted = muted.value;
      music.unlock();
      // iOS only starts audio inside the tap itself; the sync loop then keeps it in step.
      if (!muted.value && player?.playing) {
        music.sync(player.film, player.time, true, loop.value);
      }
    }
    if (player) {
      player.sound = !muted.value;
    }
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await box.value?.requestFullscreen();
      }
    } catch {
      // Refused (e.g. inside a frame): the modal stays as it is.
    }
  }

  function onFullscreenChange() {
    isFullscreen.value =
      !!document.fullscreenElement && document.fullscreenElement === box.value;
    // Fullscreen on a large screen gets the larger photos; the page size shows until they arrive.
    assets.resolve = isFullscreen.value
      ? (url) => withWidth(url, FULL_WIDTH)
      : (url) => url;
    player?.redraw();
    if (isFullscreen.value) {
      track({ action: "fullscreen" });
    }
  }

  /**
   * Phones share the film, in a full-screen view, so the slideshow pauses. Computers share
   * the link from a small card over the playing slideshow, like the settings.
   */
  function openShare() {
    settingsOpen.value = false;
    wasPlaying = sharesFiles && !!player?.playing;
    if (sharesFiles) {
      player?.pause();
    }
    sync();
    idle.value = false;
    sharing.value = true;
    if (!sharesFiles) {
      focusPanel(() => sharePanel.value);
    }
  }

  function toggleShare() {
    if (sharing.value) {
      closeShare();
    } else {
      openShare();
    }
  }

  function closeShare() {
    sharing.value = false;
    if (wasPlaying) {
      player?.play();
    }
    sync();
    // Back to the Dela button after the small card; into the viewer after the full view.
    nextTick(() =>
      (sharesFiles ? box.value : shareButton.value)?.focus({
        preventScroll: true,
      })
    );
  }

  /** The X closes an open panel first (settings, sharing), like Esc; then the viewer. */
  function closeButton() {
    if (settingsOpen.value) {
      closeSettings();
    } else if (sharing.value) {
      closeShare();
    } else if (choosing.value) {
      choosing.value = false;
    } else {
      close();
    }
  }

  /** The end card's call: the film closes on the page's own upload panel. */
  function addPhotos() {
    track({ action: "add_photos", surface: "end_card" });
    close();
    store.dispatch("createComment/toggleModal", MEDIA_INITIAL);
  }

  function close() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    }
    closeViewer();
  }

  function onKey(e: KeyboardEvent) {
    wake();
    if (e.key === "Escape") {
      e.preventDefault();
      closeButton();
      return;
    }
    const target = e.target as HTMLElement;
    if (
      e.key === " " &&
      !sharing.value &&
      !target.closest("button, input, a")
    ) {
      e.preventDefault();
      togglePlay();
    }
    // Keep focus inside the dialog, or inside the phone's share view while it covers the viewer.
    const trap = phoneShareOpen.value ? sharePhone.value?.$el : box.value;
    if (e.key === "Tab" && trap) {
      const items = Array.from(
        (trap as HTMLElement).querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"
        )
      ).filter((el) => el.offsetParent !== null);
      const [first] = items;
      const [last] = items.slice(-1);
      if (
        e.shiftKey &&
        (document.activeElement === first || document.activeElement === trap)
      ) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  }

  function onBackdrop(e: MouseEvent) {
    // A click anywhere else closes an open card (settings, sharing on a computer). The
    // event's path, not the target: a click that redraws part of the card (Kopiera länken)
    // has already removed its target from the page by the time this runs.
    const path = e.composedPath();
    const within = (el: unknown) => el instanceof Node && path.includes(el);
    if (
      settingsOpen.value &&
      !within(settingsPanel.value?.$el) &&
      !within(settingsButton.value)
    ) {
      settingsOpen.value = false;
    }
    if (
      sharing.value &&
      !sharesFiles &&
      !within(sharePanel.value?.$el) &&
      !within(shareButton.value)
    ) {
      closeShare();
    }
    if (e.target === root.value) {
      close();
    }
  }

  /** Dialog-wide listeners: activity wakes the controls, keys control playback. */
  function listen(on: boolean) {
    const method = on ? "addEventListener" : "removeEventListener";
    // Only movement over the slideshow itself brings the controls back, not over the dimmed page.
    box.value?.[method]("pointermove", wake as EventListener);
    root.value?.[method]("pointerdown", wakeFrom as EventListener);
    // Keys on the whole document while open: focus can end up outside the dialog (downloads, the share sheet).
    document[method]("keydown", onKey as EventListener);
    root.value?.[method]("click", onBackdrop as EventListener);
    stage.value?.[method]("click", onStageClick);
    stage.value?.[method]("pointerdown", onSwipeStart as EventListener);
    stage.value?.[method]("pointermove", onSwipeMove as EventListener);
    stage.value?.[method]("pointerup", onSwipeEnd as EventListener);
    stage.value?.[method]("pointercancel", onSwipeEnd as EventListener);
  }

  /** Counts opens and closes, so an open that waited for the DOM gives way to a later one. */
  let openGeneration = 0;

  async function onOpenChange(open: boolean) {
    openGeneration += 1;
    if (open) {
      const opening = openGeneration;
      await nextTick();
      if (opening !== openGeneration || !viewer.open) {
        return;
      }
      listen(true);
      if (box.value) {
        disableBodyScroll(box.value, {
          // iOS: the lock cancels touch moves inside the viewer, except on the timeline (dragged)
          // and in the phone's share view when it overflows. One that fits would hand the
          // move on to the page behind it.
          allowTouchMove: (el: EventTarget) => {
            if (!(el instanceof Element)) {
              return false;
            }
            if (el.closest(".slideshow-controls__bar")) {
              return true;
            }
            const sheet = el.closest(".slideshow-share-phone");
            return !!sheet && sheet.scrollHeight > sheet.clientHeight;
          },
          reserveScrollBarGap: true,
        });
      }
      // Focus into the dialog itself, not onto a button: no focus ring on open, and the
      // first Tab still lands on the first button.
      box.value?.focus({ preventScroll: true });
      // The film plays at once; faces and photo sizes are filled in when they arrive, so
      // nothing here waits for them.
      start();
      wake();
      if (viewer.share) {
        openShare();
      }
    } else {
      listen(false);
      stop();
      clearAllBodyScrollLocks();
      assets.resolve = (url) => url;
    }
  }

  watch(() => viewer.open, onOpenChange);
  // A shared link (…#bildspel) can ask for the viewer before this component has mounted.
  onMounted(() => {
    if (viewer.open) {
      onOpenChange(true);
    }
  });

  // Turning the phone or crossing the breakpoint changes the shape, so the film is laid out again.
  watch([format, reducedMotion], () => {
    if (player && content.value) {
      player.setReducedMotion(reducedMotion.value);
      relayout(content.value);
    }
  });

  // The film moves differently with reduced motion: made again, while the viewer is open.
  watch(reducedMotion, () => {
    shareFilm.reset();
    if (player) {
      prepareFilm();
    }
  });

  document.addEventListener("fullscreenchange", onFullscreenChange);
  document.addEventListener("visibilitychange", onVisibility);
  onBeforeUnmount(() => {
    clearTimeout(toastTimer);
    listen(false);
    stop();
    document.removeEventListener("fullscreenchange", onFullscreenChange);
    document.removeEventListener("visibilitychange", onVisibility);
  });
</script>
